import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "../App";
import { toast } from "sonner";
import LanguageSelector from "../components/LanguageSelector";
import { getTranslation } from "../i18n/translations";

const LandingPage = ({ setUser, lang = 'tr', setLang }) => {
  const navigate = useNavigate();
  const t = (key) => getTranslation(lang, key);
  
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Register state
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPhone, setRegisterPhone] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [registerPicture, setRegisterPicture] = useState("");
  const [picturePreview, setPicturePreview] = useState("");
  
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handlePictureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('imageTooBig2MB'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setRegisterPicture(reader.result);
        setPicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/login", {
        email: loginEmail,
        password: loginPassword
      });
      
      document.cookie = `session_token=${response.data.session_token}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;
      localStorage.setItem('session_token', response.data.session_token);
      
      setUser(response.data.user);
      toast.success(t('loginSuccess'));
      navigate("/dashboard");
    } catch (error) {
      let msg = error.response?.data?.detail;
      if (!msg) {
        if (error.response?.status === 401) {
          msg = t('loginErrCreds');
        } else if (error.response?.status === 404) {
          msg = t('loginErrNotFound');
        } else if (!error.response) {
          msg = t('loginErrNetwork');
        } else {
          msg = `${t('loginErrGeneric')} (${error.response.status})`;
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Password confirmation check
    if (registerPassword !== registerPasswordConfirm) {
      toast.error(t('passwordMismatch'));
      return;
    }
    
    if (registerPassword.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post("/auth/register", {
        name: registerName,
        email: registerEmail,
        phone: registerPhone || null,
        password: registerPassword,
        picture: registerPicture || null
      });
      
      document.cookie = `session_token=${response.data.session_token}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;
      localStorage.setItem('session_token', response.data.session_token);
      
      setUser(response.data.user);
      toast.success(t('registerSuccess'));
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/forgot-password", {
        email: forgotEmail
      });
      
      if (response.data.email_sent) {
        toast.success(t('resetLinkSentKnown'));
      } else {
        toast.success(t('resetLinkSentGeneric'));
      }
      
      setShowForgotPassword(false);
      setShowLogin(true);
      setForgotEmail("");
    } catch (error) {
      toast.error(t('genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Architectural grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Accent elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-amber-500/20 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-teal-500/20 rounded-full filter blur-3xl"></div>

      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector currentLang={lang} onLanguageChange={setLang} variant="dark" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <header className="text-center mb-12 fade-in">
            {/* Logo - Top Center */}
            <div className="mb-8 flex justify-center items-center">
              <img 
                src="/nkt-bee-logo.png" 
                alt="NKT - Teklif Hazırla Logo" 
                className="w-48 h-48 sm:w-[240px] sm:h-[240px] lg:w-[275px] lg:h-[275px] object-contain mx-auto drop-shadow-2xl"
              />
            </div>
            <div className="inline-block mb-4 px-6 py-2 bg-amber-500/10 backdrop-blur-sm rounded-full border border-amber-500/30">
              <span className="text-sm font-medium text-amber-400">{t('professionalSystem')}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              {t('appTitle')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">{t('appSubtitle')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-6">
              {t('appDescription')}
            </p>

            {/* Micro-commitment CTA line */}
            <div className="mb-8 flex justify-center" data-testid="hero-micro-cta">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-medium backdrop-blur-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('microCTA')}
              </div>
            </div>
            
            {/* Login/Register Buttons */}
            <div className="flex gap-4 justify-center mb-4">
              <Button
                data-testid="landing-login-btn"
                onClick={() => setShowLogin(true)}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-10 py-7 text-lg rounded-xl shadow-2xl hover:shadow-amber-500/50 font-semibold"
              >
                {t('login')}
              </Button>
              <Button
                data-testid="landing-register-btn"
                onClick={() => setShowRegister(true)}
                size="lg"
                variant="outline"
                className="border-2 border-amber-500 text-amber-400 hover:bg-amber-500/10 px-10 py-7 text-lg rounded-xl font-semibold"
              >
                {t('register')}
              </Button>
            </div>
            
            <div className="text-slate-400 text-sm mb-4">{t('or')}</div>
            
            <Button
              data-testid="google-login-button"
              onClick={handleGoogleLogin}
              size="lg"
              variant="outline"
              className="border border-slate-600 text-slate-300 hover:bg-slate-800 px-8 py-6 text-base rounded-xl"
            >
              <svg className="w-6 h-6 mr-3 inline" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
              </svg>
              {t('loginWithGoogle')}
            </Button>
          </header>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mt-24">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-slate-800/80 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-teal-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-teal-500/50">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('featureMaterialTitle')}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {t('featureMaterialDesc')}
                </p>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-slate-800/80 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-amber-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-amber-500/50">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('featureLaborTitle')}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {t('featureLaborDesc')}
                </p>
              </div>
            </div>

            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-slate-800/80 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-5 shadow-lg shadow-purple-500/50">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('featurePdfTitle')}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {t('featurePdfDesc')}
                </p>
              </div>
            </div>
          </div>

          {/* Proposal Gallery — showcase sample PDFs */}
          <section className="mt-24" aria-label={t('galleryTitle')} data-testid="proposal-gallery-section">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {t('galleryTitle')}
              </h2>
              <p className="text-slate-400 text-base max-w-2xl mx-auto">
                {t('galleryDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {[
                { key: '1', project: t('galleryProject1'), scope: t('galleryScope1'), material: 62500, labor: 48250, accent: 'from-teal-500/40 to-emerald-500/20', ring: 'hover:border-teal-500/60' },
                { key: '2', project: t('galleryProject2'), scope: t('galleryScope2'), material: 34800, labor: 42100, accent: 'from-amber-500/40 to-orange-500/20', ring: 'hover:border-amber-500/60' },
                { key: '3', project: t('galleryProject3'), scope: t('galleryScope3'), material: 41200, labor: 18700, accent: 'from-purple-500/40 to-pink-500/20', ring: 'hover:border-purple-500/60' },
              ].map((sample) => {
                const subtotal = sample.material + sample.labor;
                const vat = Math.round(subtotal * 0.20);
                const total = subtotal + vat;
                const fmt = (n) => `${t('galleryCurrency')} ${n.toLocaleString('tr-TR')}`;
                return (
                  <div key={sample.key} className="group relative" data-testid={`gallery-sample-${sample.key}`}>
                    <div className={`absolute inset-0 bg-gradient-to-br ${sample.accent} rounded-2xl blur-2xl opacity-70 group-hover:opacity-100 transition-all`}></div>
                    <div className={`relative bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700 ${sample.ring} transition-all overflow-hidden`}>
                      {/* Mock PDF paper */}
                      <div className="m-4 rounded-lg bg-white text-slate-800 shadow-2xl p-5" style={{fontFamily: 'ui-sans-serif, system-ui'}}>
                        <div className="flex items-start justify-between border-b border-slate-200 pb-3 mb-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">
                              {t('galleryBadge')}
                            </div>
                            <div className="text-sm font-bold text-slate-900 mt-1">{sample.project}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{sample.scope}</div>
                          </div>
                          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </div>
                        <div className="space-y-1.5 text-[12px]">
                          <div className="flex justify-between">
                            <span className="text-slate-600">{t('galleryMaterial')}</span>
                            <span className="font-semibold tabular-nums">{fmt(sample.material)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">{t('galleryLabor')}</span>
                            <span className="font-semibold tabular-nums">{fmt(sample.labor)}</span>
                          </div>
                          <div className="flex justify-between text-slate-500">
                            <span>{t('galleryVat')}</span>
                            <span className="tabular-nums">{fmt(vat)}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-200 flex items-baseline justify-between">
                          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                            {t('galleryTotal')}
                          </span>
                          <span className="text-lg font-bold text-slate-900 tabular-nums">{fmt(total)}</span>
                        </div>
                        {/* Decorative rows */}
                        <div className="mt-4 space-y-1 opacity-40">
                          <div className="h-1 rounded bg-slate-200 w-full"></div>
                          <div className="h-1 rounded bg-slate-200 w-5/6"></div>
                          <div className="h-1 rounded bg-slate-200 w-4/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowLogin(false)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-2xl">{t('loginTitle')}</CardTitle>
              <CardDescription>{t('loginSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">{t('email')}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showLoginPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => {
                    setShowLogin(false);
                    setShowForgotPassword(true);
                  }}
                  className="text-sm text-amber-600 hover:text-amber-700"
                >
                  {t('forgotPassword')}
                </button>
                <div className="flex gap-2">
                  <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700">
                    {loading ? t('loggingIn') : t('login')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowLogin(false)}>
                    {t('cancel')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowRegister(false)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-2xl">{t('registerTitle')}</CardTitle>
              <CardDescription>{t('registerSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">{t('fullName')}</Label>
                  <Input
                    id="register-name"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    placeholder="Ahmet Yılmaz"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">{t('email')}</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="register-phone">{t('phone')} <span className="text-slate-400 text-xs">{t('optionalWord')}</span></Label>
                  <Input
                    id="register-phone"
                    type="tel"
                    value={registerPhone}
                    onChange={(e) => setRegisterPhone(e.target.value)}
                    placeholder="+90 555 123 45 67"
                    data-testid="register-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t('minChars')}</p>
                </div>
                <div>
                  <Label htmlFor="register-password-confirm">{t('passwordConfirm')}</Label>
                  <div className="relative">
                    <Input
                      id="register-password-confirm"
                      type={showPasswordConfirm ? "text" : "password"}
                      value={registerPasswordConfirm}
                      onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswordConfirm ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {/* Profile Picture Upload */}
                <div data-testid="profile-picture-section">
                  <Label htmlFor="register-picture">{t('profilePicture') || 'Profil Resmi'}</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {picturePreview ? (
                      <div className="relative">
                        <img 
                          src={picturePreview} 
                          alt="Preview" 
                          data-testid="profile-picture-preview"
                          className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
                        />
                        <button
                          type="button"
                          data-testid="profile-picture-remove-btn"
                          onClick={() => {
                            setRegisterPicture("");
                            setPicturePreview("");
                          }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div data-testid="profile-picture-placeholder" className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                        <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <Input
                        id="register-picture"
                        data-testid="profile-picture-input"
                        type="file"
                        accept="image/*"
                        onChange={handlePictureUpload}
                        className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                      />
                      <p className="text-xs text-slate-500 mt-1" data-testid="profile-picture-max-size">{t('maxFileSize') || 'Maksimum 2MB'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700">
                    {loading ? t('registering') : t('register')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRegister(false)}>
                    {t('cancel')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowForgotPassword(false)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-2xl">{t('forgotPasswordTitle')}</CardTitle>
              <CardDescription>{t('forgotPasswordSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">{t('email')}</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={loading} className="flex-1 bg-amber-600 hover:bg-amber-700">
                    {loading ? t('sending') : t('sendLink')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setShowLogin(true);
                    }}
                  >
                    {t('backToLogin')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="relative z-10 bg-slate-900/80 backdrop-blur-sm border-t border-slate-700 mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/nkt-bee-logo.png" 
                  alt="NKT Logo" 
                  className="w-10 h-10 object-contain"
                />
                <span className="text-xl font-bold text-white">NKT - Teklif Hazırla</span>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                {t('footerDesc')}
              </p>
              <p className="text-slate-500 text-xs">
                {t('footerRights')}
              </p>
            </div>

            {/* Yardım */}
            <div>
              <h3 className="text-amber-400 font-semibold mb-4 text-lg">{t('helpTitle')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/contact" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t('contactUs')}
                  </a>
                </li>
                <li>
                  <a href="/faq" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('faqLink')}
                  </a>
                </li>
                <li>
                  <a href="/support" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('supportLink')}
                  </a>
                </li>
                <li>
                  <a href="/terms" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('termsLink')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Hizmetler */}
            <div>
              <h3 className="text-amber-400 font-semibold mb-4 text-lg">{t('servicesTitle')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('serviceCost')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('servicePdfJpg')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Topluluk */}
            <div>
              <h3 className="text-amber-400 font-semibold mb-4 text-lg">{t('communityTitle')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter / X
                  </a>
                </li>
                <li>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                    Instagram
                  </a>
                </li>
                <li>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    YouTube
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-slate-500 text-sm">
                {t('footerSlogan')}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                Kurtuluş Mh. 64016 Bina No:20 Kat:0 Daire No:1 Seyhan/Adana
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+905323468920" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                0532 346 89 20
              </a>
              <a href="mailto:info@nktteklif.com" className="text-slate-400 hover:text-amber-400 transition text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                info@nktteklif.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;